import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchDeviceFloorplanPlacements,
  createDeviceFloorplanPlacement,
} from 'scenes/Renolog/services/deviceFloorplanPlacements.service';
import { postCreateFloorplanPlacement } from 'store/floorplans/services/postCreateFloorplanPlacement.service';
import { deleteFloorplanPlacement as deleteFloorplanPlacementService } from 'store/floorplans/services/deleteFloorplanPlacement.service';
import { patchFloorplanPlacement } from 'store/floorplans/services/patchFloorplanPlacement.service';
import { getProjectFloorplanPlacements } from 'store/floorplans/services/getProjectFloorplanPlacements.service';

/**
 * Fetch sensor floorplan placements for a project
 */
export const fetchSensorFloorplanPlacements = createAsyncThunk(
  'floorplanPlacements/fetchSensorFloorplanPlacements',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      const placements = await fetchDeviceFloorplanPlacements({
        projectId,
      });
      return placements;
    } catch (error) {
      return rejectWithValue(
        error.message || 'Failed to fetch floorplan placements'
      );
    }
  }
);

/**
 * Fetch image placements for a project
 */
export const fetchImageFloorplanPlacements = createAsyncThunk(
  'floorplanPlacements/fetchImageFloorplanPlacements',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      const response = await getProjectFloorplanPlacements(projectId);
      return response.placements || [];
    } catch (error) {
      return rejectWithValue(
        error.message || 'Failed to fetch image floorplan placements'
      );
    }
  }
);

/**
 * Create a new floorplan placement (sensor or image)
 * For sensors: pass { deviceId, floorplanId, x, y }
 * For images: pass { filePermissionId(s), floorplanId, x, y, direction? }
 */
export const createFloorplanPlacement = createAsyncThunk(
  'floorplanPlacements/createFloorplanPlacement',
  async (payload, { rejectWithValue }) => {
    try {
      const {
        projectId,
        deviceId,
        filePermissionId,
        filePermissionIds,
        floorplanId,
        x,
        y,
        direction,
      } = payload;

      let response;
      if (deviceId) {
        // Sensor placement
        response = await createDeviceFloorplanPlacement({
          projectId,
          deviceId,
          floorplanId,
          x,
          y,
        });
        return response.placements ? response.placements[0] : response;
      } else {
        // Image placement
        response = await postCreateFloorplanPlacement(projectId, {
          floorplanId,
          x,
          y,
          ...(filePermissionIds ? { filePermissionIds } : { filePermissionId }),
          ...(direction !== undefined ? { direction } : {}),
        });
        return response.placements;
      }
    } catch (error) {
      return rejectWithValue(
        error.message || 'Failed to create floorplan placement'
      );
    }
  }
);

/**
 * Update an existing floorplan placement
 * For sensors: pass { projectId, placementId, x, y }
 * For images: pass { projectId, placementId, x, y, direction? }
 */
export const updateFloorplanPlacement = createAsyncThunk(
  'floorplanPlacements/updateFloorplanPlacement',
  async ({ projectId, placementId, x, y, direction }, { rejectWithValue }) => {
    try {
      let placement;
      // Image placement with optional direction parameter
      const updateParams = { x, y };
      if (direction !== undefined) updateParams.direction = direction;
      placement = await patchFloorplanPlacement(
        projectId,
        placementId,
        updateParams
      );

      return placement;
    } catch (error) {
      return rejectWithValue(
        error.message || 'Failed to update floorplan placement'
      );
    }
  }
);

/**
 * Delete a floorplan placement
 */
export const deleteFloorplanPlacement = createAsyncThunk(
  'floorplanPlacements/deleteFloorplanPlacement',
  async ({ projectId, placementId }, { rejectWithValue }) => {
    try {
      await deleteFloorplanPlacementService(projectId, placementId);

      return placementId;
    } catch (error) {
      return rejectWithValue(
        error.message || 'Failed to delete floorplan placement'
      );
    }
  }
);
